/**
 * SitePick front — 「自社の情報で見る」の画像の受け取り（写真とロゴの共通の経路。docs/22 §4.5）。
 *
 *  1. 大きさ（bytes）→ 先頭の数バイト（magic bytes）で JPEG / PNG / WebP だけを通す。SVG / XML は受け付けない。
 *  2. ヘッダから寸法を読む（JPEG は APP セグメントを辿って最初の SOF0/1/2・WebP は VP8X → VP8L → VP8・PNG は IHDR）。
 *     画素数の上限は **decode の前に** 判定する。
 *  3. createImageBitmap（EXIF の向き・縮小）→ canvas → 再エンコード（WebP。出ない環境は写真 = JPEG / 透過のあるロゴ = PNG）。
 * 返すのは Blob と寸法だけ。サーバへは送らない。ファイル名は扱わない。
 */

const TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

/**
 * @param {string} code image_type | image_too_large | image_pixels | image_decode
 * @returns {Error}
 */
const fail = (code) => {
  const e = new Error(code);
  e.code = code;
  return e;
};

/**
 * @param {Blob} file
 * @param {number} start
 * @param {number} end
 * @returns {Promise<Uint8Array>}
 */
async function readBytes(file, start, end) {
  const buf = await file.slice(start, end).arrayBuffer();
  return new Uint8Array(buf);
}

/**
 * SVG / XML の文書か（先頭の BOM・空白を飛ばして `<svg` / `<?xml` / `<!DOCTYPE svg` を見る）。
 *
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function looksLikeSvg(bytes) {
  let i = 0;
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    i = 3;
  }
  while (i < bytes.length && (bytes[i] === 0x20 || bytes[i] === 0x09 || bytes[i] === 0x0a || bytes[i] === 0x0d)) {
    i += 1;
  }
  let head = "";
  for (let j = i; j < Math.min(bytes.length, i + 16); j += 1) {
    head += String.fromCharCode(bytes[j]);
  }
  return /^<(?:svg|\?xml|!doctype\s+svg)/i.test(head);
}

/**
 * 先頭の bytes から形式を判定する（拡張子・File.type は信用しない）。
 *
 * @param {Uint8Array} bytes
 * @returns {string} image/jpeg | image/png | image/webp | ""（それ以外。SVG を含む）
 */
export function sniff(bytes) {
  if (looksLikeSvg(bytes)) {
    return "";
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return "image/png";
  }
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
    return "image/webp";
  }
  return "";
}

/**
 * ヘッダから寸法と透過の有無を読む（decode しない）。
 *
 * @param {Blob} file
 * @param {string} type
 * @returns {Promise<{width: number, height: number, alpha: boolean}>}
 */
async function inspect(file, type) {
  if (type === "image/png") {
    const b = await readBytes(file, 0, Math.min(file.size, 8192));
    if (b.length < 26) {
      return { width: 0, height: 0, alpha: false };
    }
    const dv = new DataView(b.buffer);
    const colorType = b[25];
    // 4 = グレー + α / 6 = RGBA。パレット（3）は tRNS チャンクがあれば透過あり。
    let alpha = colorType === 4 || colorType === 6;
    if (!alpha && colorType === 3) {
      let text = "";
      for (let i = 0; i < b.length; i += 1) {
        text += String.fromCharCode(b[i]);
      }
      const t = text.indexOf("tRNS");
      const d = text.indexOf("IDAT");
      alpha = t !== -1 && (d === -1 || t < d);
    }
    return { width: dv.getUint32(16), height: dv.getUint32(20), alpha };
  }
  if (type === "image/webp") {
    const b = await readBytes(file, 0, 30);
    const tag = String.fromCharCode(b[12], b[13], b[14], b[15]);
    if (tag === "VP8X") {
      return {
        width: 1 + (b[24] | (b[25] << 8) | (b[26] << 16)),
        height: 1 + (b[27] | (b[28] << 8) | (b[29] << 16)),
        alpha: (b[20] & 0x10) !== 0,
      };
    }
    if (tag === "VP8L") {
      const bits = (b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24)) >>> 0;
      return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1, alpha: ((bits >>> 28) & 1) === 1 };
    }
    if (tag === "VP8 ") {
      return { width: (b[26] | (b[27] << 8)) & 0x3fff, height: (b[28] | (b[29] << 8)) & 0x3fff, alpha: false };
    }
    return { width: 0, height: 0, alpha: false };
  }
  // JPEG: セグメント長を辿り、APP 系（EXIF 内サムネイルの SOF を含む）を飛ばして最初の SOF0/1/2。
  let pos = 2;
  const limit = Math.min(file.size, 4 * 1024 * 1024);
  while (pos < limit) {
    const h = await readBytes(file, pos, pos + 9);
    if (h.length < 4) {
      break;
    }
    if (h[0] !== 0xff) {
      pos += 1;
      continue;
    }
    const marker = h[1];
    if (marker === 0xff) {
      pos += 1;
      continue;
    }
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      pos += 2;
      continue;
    }
    if (marker === 0xc0 || marker === 0xc1 || marker === 0xc2) {
      return { height: (h[5] << 8) | h[6], width: (h[7] << 8) | h[8], alpha: false };
    }
    if (marker === 0xda || marker === 0xd9) {
      break;
    }
    pos += 2 + ((h[2] << 8) | h[3]);
  }
  return { width: 0, height: 0, alpha: false };
}

/**
 * 画像を検査・縮小・再エンコードする。
 *
 * @param {File} file
 * @param {{maxBytes: number, maxPixels: number, maxEdge: number, outMaxBytes: number, keepAlpha?: boolean}} opts
 * @returns {Promise<{blob: Blob, type: string, bytes: number, width: number, height: number}>}
 */
export async function prepareImage(file, opts) {
  if (!(file instanceof Blob) || file.size <= 0) {
    throw fail("image_type");
  }
  if (file.size > opts.maxBytes) {
    throw fail("image_too_large");
  }
  const head = await readBytes(file, 0, 32);
  const type = sniff(head);
  if (!TYPES.has(type)) {
    throw fail("image_type");
  }
  const info = await inspect(file, type);
  if (info.width && info.height && info.width * info.height > opts.maxPixels) {
    throw fail("image_pixels");
  }
  const alpha = !!opts.keepAlpha && info.alpha;

  const scale = info.width && info.height ? Math.min(1, opts.maxEdge / Math.max(info.width, info.height)) : 1;
  const bitmapOpts = { imageOrientation: "from-image", resizeQuality: "high" };
  if (scale < 1) {
    bitmapOpts.resizeWidth = Math.max(1, Math.round(info.width * scale));
    bitmapOpts.resizeHeight = Math.max(1, Math.round(info.height * scale));
  }
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, bitmapOpts);
  } catch {
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      throw fail("image_decode");
    }
  }
  const s2 = Math.min(1, opts.maxEdge / Math.max(bitmap.width, bitmap.height));
  const cw = Math.max(1, Math.round(bitmap.width * s2));
  const ch = Math.max(1, Math.round(bitmap.height * s2));
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    if (typeof bitmap.close === "function") {
      bitmap.close();
    }
    throw fail("image_decode");
  }
  ctx.drawImage(bitmap, 0, 0, cw, ch);
  if (typeof bitmap.close === "function") {
    bitmap.close();
  }

  const toBlob = (mime, quality) => new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
  let blob = await toBlob("image/webp", 0.85);
  if (!blob || blob.type !== "image/webp") {
    // WebKit は WebP を返さず PNG になる。写真は JPEG に、透過のあるロゴは透過を保つため PNG にする。
    blob = alpha ? await toBlob("image/png") : await toBlob("image/jpeg", 0.85);
  }
  if (blob && blob.size > opts.outMaxBytes && blob.type !== "image/png") {
    blob = await toBlob(blob.type, 0.7);
  }
  canvas.width = 0;
  canvas.height = 0;
  if (!blob || !TYPES.has(blob.type)) {
    throw fail("image_decode");
  }
  if (blob.size > opts.outMaxBytes) {
    throw fail("image_too_large");
  }
  return { blob, type: blob.type, bytes: blob.size, width: cw, height: ch };
}

/**
 * 縦横比の表記（例 3:2）と向き。
 *
 * @param {number} width
 * @param {number} height
 * @returns {{ratio: string, orientation: string}} orientation = wide | tall | square | ""
 */
export function ratioOf(width, height) {
  if (!width || !height) {
    return { ratio: "", orientation: "" };
  }
  const gcd = (a, b) => (b ? gcd(b, a % b) : a);
  const d = gcd(width, height);
  const rw = width / d;
  const rh = height / d;
  const ratio = rw <= 20 && rh <= 20 ? `${rw}:${rh}` : `${(width / height).toFixed(2)}:1`;
  return { ratio, orientation: width > height ? "wide" : width < height ? "tall" : "square" };
}
